import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import Lightbulb from '@mui/icons-material/Lightbulb';
import AccountMenu from './AccountMenu';
import Link from '@mui/material/Link';
import { actions } from '../../actions';
import { CHATS } from '../../constants/routes';
import Button from '@mui/material/Button';
import collections from '../../constants/FirestoreCollections';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Hidden,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  ArrowBack,
  ArrowForward,
  ExitToApp,
  FilterList,
  Info,
  SupportAgent,
} from '@mui/icons-material';
import { purple } from '@mui/material/colors';
import ChatsFilter from '../../screens/Chats/components/ChatsFilter';
import useRoutes from '../../hooks/useRoutes';
import RecommendationPopup from '../../shared/Popups/Recommendation';
import { ITheme } from '../../theme';
import useTemplates from '../../hooks/db/useTemplates';
import AlertComponent from '../../screens/ClientConfiguration/components/AlertComponente';

const useStyles = (small: boolean) => {
  return makeStyles((theme: ITheme) => ({
    toolbar: {
      paddingRight: 24,
      display: 'flex',
      position: 'relative',
    },
    iconNavbar: {
      margin: small ? '0 auto' : '',
      transition: 'all .3s',
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.2)',
      },
    },
    filterIcon: {
      color: theme.palette.white.main,
      padding: '12px',
    },
    gobackIcon: { width: '50px', marginRight: '150px' },
    gobackChat: {
      color: theme.palette.white.main,
      position: 'absolute',
      left: '16px',
      top: 'calc(50% - 12px)',
      padding: 0,
    },
    infoIcon: {
      color: theme.palette.white.main,
      position: 'absolute',
      right: '16px',
    },
    filterIconLy: {
      backgroundColor: purple[500],
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '5px',
    },
    toolbarIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '0 8px',
      // ...theme.mixins.toolbar,
    },
    appBarShift: {
      backgroundColor: theme.palette.black.main,
      width: '100%',
      // transition: theme.transitions.create(['width', 'margin'], {
      //   easing: theme.transitions.easing.sharp,
      //   duration: theme.transitions.duration.enteringScreen,
      // }),
      userSelect: 'none',
    },
    title: {
      marginLeft: '10px',
      color: theme.palette.white.main,
    },
    notificactionClicked: {
      color: theme.palette.primary.main,
    },
    notificaction: {
      color: !small ? theme.palette.white.main : '',
    },
    supportChatBotActive: {
      backgroundColor: theme.palette.primary.main,
      color: !small ? theme.palette.white.main + ' !important' : '',
      '&:hover': {
        backgroundColor: theme.palette.primary.main + ' !important',
        color: !small ? theme.palette.white.main + ' !important' : '',
      },
    },
    supportChatBot: {
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main + ' !important',
      },
    },
    buttonRoot: {
      color: theme.palette.white.main,
    },
    avatar: {
      // color: theme.palette.getContrastText(purple[500]),
      backgroundColor: purple[500],
    },
    adminRef: {
      fontSize: theme.sizes.sm.t1,
      color: '#ccc',
    },
    dangerBtn: {
      border: '1px solid red',
      color: 'red',
      '&:hover': {
        borderColor: 'red',
      },
    },
    profileButton: {
      marginLeft: 16,
      width: 50,
      height: 50,
      borderRadius: '3rem',
      cursor: 'pointer',
    },
    profileButtonImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '3rem',
    },
    drawerItem: {
      padding: 8,
      display: 'flex',
    },
  }));
};

function DisplayAvatar() {
  const profile = useSelector((state: any) => state.firebase.profile);
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();

  if (profile.avatarUrl) {
    return <Avatar alt={profile.displayName || 'Avatar'} src={profile.avatarUrl} />;
  }

  if (profile.displayName) {
    const initials = String(profile.displayName)
      .toUpperCase()
      .split(' ')
      .map((n) => n[0])
      .join('');
    return <Avatar className={classes.avatar}>{initials}</Avatar>;
  }

  return (
    <Avatar className={classes.avatar}>
      <AccountCircle />
    </Avatar>
  );
}

export default function Navbar() {
  const small = useMediaQuery('(max-width:600px)');
  const [drawer, setDrawer] = useState<number | null>(null);
  const [prestobotsConfig, setPrestobotsConfig] = useState<any>();
  const [recommendationPopup, setRecommendationPopup] = useState(false);
  const classes = useStyles(small)();
  const firebase = useFirebase();
  const appDispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const profile = useSelector((state: any) => state.firebase.profile);
  const infoChat = useSelector((state: any) => state.app.infoChat);
  const filter = useSelector((state: any) => state.app.filter);
  const firestore = useFirestore();
  const chatsNoEnded = useSelector((state: any) => state.app.ordered.chatsNoEnded);
  const countChats = useSelector((state: any) => state.app.countChats);
  const organization = useSelector((state: any) => state.app.organization);
  const chatId = useSelector((state: any) => state.app.activeChat);
  const isSupportChatBotOpen = useSelector((state: any) => state.app.isSupportChatBotOpen);
  const routes = useRoutes();
  const { getTemplates } = useTemplates();

  const showChatsNoEnded = useSelector((state: any) => state.app.activeChatShowChatsNoEnded);

  const { healthSoftware, services } = organization || {};

  useEffect(() => {
    setDrawer(null);
  }, [filter.query.date]);

  useEffect(() => {
    getTemplates().then((templates) => {
      appDispatch({ type: actions.templates, templates });
    });
  }, [firestore]);

  useEffect(() => {
    const unsubscribe = firestore
      .collection(collections.configurations)
      .where('name', '==', 'prestobots')
      .onSnapshot((snapshot) => {
        if (!snapshot.empty) {
          const config = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
          setPrestobotsConfig(config);
          appDispatch({ type: actions.prestobotsConfig, config });
        }
      });

    return () => {
      unsubscribe && unsubscribe();
    };
  }, [firestore, profile.organizationId]);

  // dialog organization START
  const [open, setOpen] = React.useState(false);

  const handleTrainingChatsClick = (value: boolean) => {
    const training = value === filter.local.training ? -1 : value;
    if (training) {
      appDispatch({ type: actions.activeChatShowChatsNoEnded, show: false });
    }
    appDispatch({ type: actions.filterTraining, training });
  };

  const handleChatsNoEndedClick = () => {
    if (drawer != null) {
      setDrawer(null);
    }
    if (location.pathname !== CHATS) {
      if (!showChatsNoEnded) {
        appDispatch({ type: actions.filterTraining, training: -1 });
        appDispatch({ type: actions.activeChatShowChatsNoEnded, show: true });
      }
      history.push(CHATS);
    } else {
      if (!showChatsNoEnded) {
        appDispatch({ type: actions.filterTraining, training: -1 });
      }
      appDispatch({ type: actions.activeChatShowChatsNoEnded, show: !showChatsNoEnded });
    }
  };

  const goTo = (path: string) => {
    setOpen(false);
    history.push(path);
  };

  const handleGoBack = () => {
    infoChat
      ? appDispatch({ type: actions.infoChat, id: false })
      : appDispatch({ type: actions.activeChat, id: null });
  };
  const handleInfoClick = () => {
    appDispatch({ type: actions.infoChat, infoChat: !infoChat });
  };
  const changeOpenRecommendation = () => {
    setRecommendationPopup(!recommendationPopup);
  };

  const isAdmin = profile && profile.claims && profile.claims.includes('admin');
  const stoppedNotificationsService =
    organization.stoppedNotificationsService ||
    (prestobotsConfig && prestobotsConfig.stoppedNotificationsService);

  const image = profile && profile.image;

  const handleCopyId = () => {
    navigator.clipboard.writeText(organization.id);
    appDispatch({
      type: actions.alert,
      alert: {
        snackbarOpen: true,
        snackbarMessage: `ID copiado en portapapeles`,
        snackbarSeverity: 'info',
      },
    });
  };

  function handleLogout() {
    return firebase.logout().then(() => {
      firebase.dispatch({
        type: actions.clear,
      });
      history.push('/');
    });
  }

  const shouldShowChatBot = () => {
    const isMacenaSoftware = healthSoftware?.name === 'macena';
    const hasSupportBotService = services?.supportBot;
    return isMacenaSoftware && hasSupportBotService;
  };
  const handleSupportChatBot = () => {
    const dfMessengerBubble = document.querySelector('df-messenger-chat-bubble');
    if (isSupportChatBotOpen) {
      // @ts-ignore
      dfMessengerBubble?.closeChat();
      appDispatch({ type: actions.isSupportChatBotOpen, isSupportChatBotOpen: false });
    } else {
      // @ts-ignore
      dfMessengerBubble?.openChat();
      appDispatch({ type: actions.isSupportChatBotOpen, isSupportChatBotOpen: true });
    }
  };

  return (
    <AppBar className={classes.appBarShift} sx={{ userSelect: 'none' }}>
      <Toolbar className={classes.toolbar}>
        {recommendationPopup && <RecommendationPopup handleClose={changeOpenRecommendation} />}
        {!chatId && (
          <Hidden smUp>
            <IconButton onClick={() => setDrawer(0)} className={classes.filterIcon} size="large">
              <Box className={classes.filterIconLy}>
                <FilterList />
              </Box>
            </IconButton>
          </Hidden>
        )}
        <Hidden smUp>
          {/* Como en mobile se desmonta el componente lo monto invisible para las alertas */}
          <div style={{ display: 'none' }}>{drawer === null && <AlertComponent />}</div>
        </Hidden>
        {chatId && (
          <Hidden smUp>
            <IconButton className={classes.gobackChat} onClick={() => handleGoBack()} size="large">
              <ArrowBack />
            </IconButton>
          </Hidden>
        )}
        <Link className={classes.iconNavbar} onClick={() => goTo('/')}>
          <img src="/logo512.png" width="40" alt="PrestoBots" />
        </Link>
        {small && chatId && (
          <IconButton className={classes.infoIcon} onClick={handleInfoClick} size="large">
            <Info />
          </IconButton>
        )}
        {!chatId && (
          <Hidden smUp>
            <IconButton onClick={() => setDrawer(1)} size="large">
              <DisplayAvatar />
            </IconButton>
            <Drawer anchor="left" open={drawer === 0}>
              <IconButton onClick={() => setDrawer(null)} className={classes.gobackIcon}>
                <ArrowBack />
              </IconButton>
              <Box
                p={2}
                className={
                  showChatsNoEnded ? `${classes.notificactionClicked}` : `${classes.notificaction}`
                }
                onClick={handleChatsNoEndedClick}
              >
                <Badge color="secondary">
                  <HourglassTopIcon />
                </Badge>
              </Box>
              <AlertComponent />
              <ChatsFilter closeMenu={() => setDrawer(null)} />
            </Drawer>
            <Drawer anchor="right" open={drawer === 1}>
              <IconButton onClick={() => setDrawer(null)} className={classes.gobackIcon}>
                <ArrowForward />
              </IconButton>
              <Divider />
              {profile && profile.isLoaded && profile.claims && isAdmin && (
                <Box display="flex" flexDirection="column" textAlign="center">
                  {routes &&
                    routes
                      .filter((route) => !route.adminRoutes)
                      .map((route) => (
                        <ButtonBase
                          key={route.path}
                          onClick={() => {
                            goTo(route.path);
                            setDrawer(null);
                          }}
                          className={classes.drawerItem}
                        >
                          {route.icon}
                          <Box flex={1}>
                            <Typography>{route.label}</Typography>
                          </Box>
                        </ButtonBase>
                      ))}
                  <ButtonBase onClick={handleLogout} className={classes.drawerItem}>
                    <ExitToApp />
                    <Box flex={1}>
                      <Typography>Salir</Typography>
                    </Box>
                  </ButtonBase>
                </Box>
              )}
            </Drawer>
          </Hidden>
        )}

        <Hidden smDown>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
            onClick={() => goTo('/')}
            style={{ cursor: 'pointer' }}
          >
            PrestoBots
          </Typography>
          <Box flexGrow={1} />
          {shouldShowChatBot() && (
            <IconButton
              className={
                isSupportChatBotOpen
                  ? `${classes.supportChatBotActive}`
                  : `${classes.supportChatBot}`
              }
              onClick={() => handleSupportChatBot()}
              size="large"
            >
              <Tooltip title="Bot de soporte" arrow>
                <SupportAgent />
              </Tooltip>
            </IconButton>
          )}
          {isAdmin && stoppedNotificationsService && (
            <Box mr={2}>
              <Button variant="outlined" className={classes.dangerBtn}>
                Servicio detenido
              </Button>
            </Box>
          )}
          {isAdmin && organization && (
            <Box ml={2} onClick={handleCopyId}>
              <Button variant="outlined" color="primary">
                {organization.name}
              </Button>
            </Box>
          )}
          <IconButton
            className={
              filter.local.training !== -1
                ? `${classes.notificactionClicked}`
                : `${classes.notificaction}`
            }
            onClick={() => handleTrainingChatsClick(true)}
            size="large"
          >
            <Tooltip title="Chats entrenamiento" arrow>
              <Badge
                badgeContent={filter.local.training !== -1 ? countChats.training : null}
                color="primary"
              >
                <Lightbulb />
              </Badge>
            </Tooltip>
          </IconButton>
          <IconButton
            className={
              showChatsNoEnded ? `${classes.notificactionClicked}` : `${classes.notificaction}`
            }
            onClick={handleChatsNoEndedClick}
            size="large"
          >
            <Tooltip title="Filtro por hacer" arrow>
              <Badge
                color="primary"
                badgeContent={showChatsNoEnded ? chatsNoEnded.length || 0 : null}
              >
                <HourglassTopIcon />
              </Badge>
            </Tooltip>
          </IconButton>
          <AlertComponent />
          <Box
            className={classes.profileButton}
            display="flex"
            alignItems="center"
            onClick={() => {
              setTimeout(() => {
                setOpen(true);
              }, 10);
            }}
          >
            {image ? (
              <img className={classes.profileButtonImg} src={image} />
            ) : (
              <Avatar className={classes.avatar}>
                <AccountCircle />
              </Avatar>
            )}
          </Box>
          <AccountMenu open={open} setOpen={setOpen} />
        </Hidden>
      </Toolbar>
    </AppBar>
  );
}
